import React, { useCallback } from 'react';
import { Box, Grid } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import i18n from '../../../i18n';
import { connect, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { useActionDispatchOnMount } from 'core/store/store';

import {
    registrationErrorMessageSelector,
    registrationSettingsDeliveryInfoSelector,
    registrationSettingsPaymentCardSelector,
    registrationSettingsPaymentCashSelector,
    registrationSettingsPickUpInfoSelector,
    registrationSettingsYandexCheckoutSelector,
} from 'state/registration/selector';
import { makeRegistrationStoreSettingsFetchAction } from 'state/registration/actions';

import { Text, TextTypography } from 'components/Text/Text';
import { ErrorBoundary } from 'components/ErrorBoundary';
import { InfoButton } from 'components/InfoButton/InfoButton';
import { FormErrorMessage } from 'components/FormErrorMessage/FormErrorMessage';

import { ReactComponent as PickUpIcon } from 'assets/glyphs/pick-up.svg';
import { ReactComponent as DeliveryIcon } from 'assets/glyphs/delivery.svg';
import { ReactComponent as CashIcon } from 'assets/glyphs/cash.svg';
import { ReactComponent as CardsIcon } from 'assets/glyphs/cards.svg';
import { ReactComponent as OnlineIcon } from 'assets/glyphs/online.svg';
import { RoutePath } from 'RoutePath';
import { SwitchControlField } from 'components/SwitchControl/SwitchControl';
import { authStoreOpenForOrdersSelector } from 'state/auth/selectors';
import { HorizontalDivider } from 'components/HorizontalDivider/HorizontalDivider';
import { Field, formValueSelector, reduxForm } from 'redux-form';
import { Form } from 'forms/types';
import { RegistrationSlice } from 'state/registration/reducer';
import { LargeButton, LargeButtonColor } from 'components/LargeButton/LargeButton';
import { isUIEnabledSelector } from 'state/app/selectors';
import { RichTextControlField } from 'components/RichTextControl/RichTextControl';

const useStyles = makeStyles(theme => ({
    containerForm: {
        display: 'flex',
        justifyContent: 'center',
        maxWidth: 800,
        margin: '0 auto',
        paddingBottom: 130,
    },
    content: {
        margin: '0 auto',
        background: '#FDFFFC',
        boxShadow: '0px 0px 50px rgba(15, 24, 37, 0.05)',
        borderRadius: 20,
        marginTop: theme.spacing(4),
        flex: 1,
        padding: theme.spacing(3),
        [theme.breakpoints.down(640)]: {
            boxShadow: 'none',
        },
        [theme.breakpoints.down(480)]: {
            marginTop: theme.spacing(2),
            borderRadius: 0,
        },
        [theme.breakpoints.down(375)]: {
            marginTop: -8,
        },
    },
    settingsItem: {
        minWidth: 332,
        paddingTop: theme.spacing(1.5),
        flexBasis: '100%',
        paddingBottom: theme.spacing(1.5),
        paddingRight: theme.spacing(1.5),
        paddingLeft: theme.spacing(1.5),
        [theme.breakpoints.down(640)]: {
            width: '100%',
            minWidth: '100%',
        },
        [theme.breakpoints.down(760)]: {
            minWidth: 244,
        },
        [theme.breakpoints.up(960)]: {
            minWidth: 380,
        },
        [theme.breakpoints.up(1280)]: {
            minWidth: 332,
            flexGrow: 1,
            maxWidth: '50%',
        },
        [theme.breakpoints.up(1920)]: {
            flexGrow: 1,
            maxWidth: '33%',
        },
    },
    text: {
        marginBottom: theme.spacing(2),
        marginLeft: theme.spacing(0.5),
    },
    link: {
        textDecoration: 'none',
    },
    field: {
        paddingBottom: theme.spacing(2),
        whiteSpace: 'pre-wrap',
    },
    wysiwygField: {
        paddingBottom: theme.spacing(2),
        width: '100%',
    },
    footer: {
        flexGrow: 0,
        flexBasis: 'auto',
    },
    footerContent: {
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(2),
        paddingTop: theme.spacing(2),
        paddingBottom: theme.spacing(3),
    },
    footerControls: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingTop: theme.spacing(1),
        paddingRight: theme.spacing(2),
        paddingLeft: theme.spacing(2),
        width: '100%',
    },
    footerControlsButton: {
        width: 164,
        height: 48,
    },
    [theme.breakpoints.up('sm')]: {
        paymentItem: {
            paddingRight: theme.spacing(1.5),
            paddingLeft: theme.spacing(1.5),
        },
    },
    noOrders: {
        backgroundColor: (props: { openForOrders: boolean }) =>
            props.openForOrders ? theme.palette.brandPrimary.lightest : 'rgba(255, 114, 0, 0.15)',
        borderRadius: theme.spacing(1.25),
        paddingLeft: theme.spacing(2),
        paddingBottom: theme.spacing(1),
        border: (props: { openForOrders: boolean }) => (props.openForOrders ? '' : '1px solid rgba(255, 114, 0, 0.15)'),
    },
    noOrdersControl: {
        paddingTop: theme.spacing(2),
        paddingBottom: theme.spacing(1),
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    noOrdersText: {
        paddingBottom: theme.spacing(1),
        paddingRight: theme.spacing(2),
    },
    line: {
        marginTop: theme.spacing(3),
        marginBottom: theme.spacing(3),
    },
    infoContainer: {
        marginTop: 8,
        paddingLeft: 56,
    },
    subTitle: {
        marginTop: theme.spacing(1),
    },
    footerInfo: {
        width: '100%',
        position: 'fixed',
        bottom: 0,
        zIndex: 1,
        background: '#fafafa',
        [theme.breakpoints.down(640)]: {
            padding: theme.spacing(0, 0, 8),
        },
    },
    editSubmit: {
        maxWidth: 800,
        margin: 'auto',
        padding: theme.spacing(3, 0),
        display: 'flex',
        alignItems: 'center',
        backgroundColor: '#fafafa',
        [theme.breakpoints.down(960)]: {
            padding: theme.spacing(3, 5),
        },
        [theme.breakpoints.down(640)]: {
            padding: theme.spacing(3),
        },
        [theme.breakpoints.down(480)]: {
            padding: theme.spacing(2, 2),
        },
    },
}));

interface SettingsFormProps {
    submitButtonText: string;
    onPickupClick: () => void;
    onDeliveryClick: () => void;
    onCashClick: () => void;
    onCardClick: () => void;
    onOnlineClick: () => void;
    onLoginAsCustomerClick: () => void;
}

const storeDetailsEditFormValueSelector = formValueSelector(Form.STORE_SETTING_EDIT_FORM);

const mapStateToEditFormProps = (state: RegistrationSlice) => {
    const paymentComment = state.registration.settings.paymentComment;
    const deliveryComment = state.registration.settings.deliveryComment;

    const enabledSpecificDelivery = state.registration.settings.delivery.enabledSpecificDelivery;

    return {
        initialValues: {
            paymentComment: paymentComment,
            deliveryComment: deliveryComment,
            enabledSpecificDelivery,
            // enabledSpecificDelivery: deliverySwitch,
            // enabledSpecificDelivery: mergeWith(
            //     {},
            //     enabledSpecificDelivery,
            //     state.registration.settings.delivery,
            //     (o, s) => (s === null ? o : s),
            // ),
        },
        enabledSpecificDelivery,
        paymentComment,
        deliveryComment,
    };
};
const inputSelector = (state: any) => {
    const paymentComment = storeDetailsEditFormValueSelector(state, 'paymentComment');
    const deliveryComment = storeDetailsEditFormValueSelector(state, 'deliveryComment');

    const paymentCommentValue = paymentComment ? String(paymentComment).length : '0';
    const deliveryCommentValue = deliveryComment ? String(deliveryComment).length : '0';

    return {
        paymentCommentValue,
        deliveryCommentValue,
    };
};

export const DefaultStoreSettingsForm = (props: SettingsFormProps & any) => {
    const openForOrders = useSelector(authStoreOpenForOrdersSelector);
    const classes = useStyles({ openForOrders });
    const { t } = i18n.useTranslation();
    const history = useHistory();
    const isUIEnabled = useSelector(isUIEnabledSelector);

    const goToVendorDashboard = useCallback(() => history.push(RoutePath.VENDOR_DASHBOARD), [history]);

    const deliveryFormValues = useSelector(registrationSettingsDeliveryInfoSelector);
    const pickUpFormValues = useSelector(registrationSettingsPickUpInfoSelector);
    const cashPayment = useSelector(registrationSettingsPaymentCashSelector);
    const cardPayment = useSelector(registrationSettingsPaymentCardSelector);
    const yandexCheckoutFormValues = useSelector(registrationSettingsYandexCheckoutSelector);

    const deliveryOperator = deliveryFormValues.enabledServiceDelivery
        ? t('onboarding:settings.deliveryService')
        : deliveryFormValues.enabledFreeShipping ||
          deliveryFormValues.enabledPayShipping ||
          deliveryFormValues.enabledSpecificDelivery
        ? t('onboarding:settings.ownDelivery')
        : null;

    const deliveryInfo =
        deliveryFormValues.enabledFreeShipping ||
        deliveryFormValues.enabledPayShipping ||
        deliveryFormValues.enabledServiceDelivery ||
        deliveryFormValues.enabledSpecificDelivery
            ? `${deliveryOperator} ${
                  (deliveryFormValues.enabledFreeShipping ? t('onboarding:settings.freeDelivery') : '') +
                  (deliveryFormValues.enabledFreeShipping && deliveryFormValues.enabledPayShipping ? '/' : '') +
                  (deliveryFormValues.enabledPayShipping ? t('onboarding:settings.payDelivery') : '') +
                  (deliveryFormValues.enabledSpecificDelivery ? t('onboarding:settings.specificDelivery') : '')
              }`
            : null;

    const pickUpInfo = pickUpFormValues.enabled
        ? t('onboarding:settings.pickupInfo', { count: Number(pickUpFormValues.hoursToStore) })
        : null;

    const cashInfo =
        (cashPayment.atStore ? t('onboarding:settings:atStore') : '') +
        (cashPayment.atStore && cashPayment.atDelivery ? ' + ' : '') +
        (cashPayment.atDelivery ? t('onboarding:settings:atDelivery') : '');

    const cardInfo =
        (cardPayment.atStore ? t('onboarding:settings:atStore') : '') +
        (cardPayment.atStore && cardPayment.atDelivery ? ' + ' : '') +
        (cardPayment.atDelivery ? t('onboarding:settings:atDelivery') : '');

    const errorMessage = useSelector(registrationErrorMessageSelector);

    const { handleSubmit } = props;

    const inputs = useSelector(inputSelector);
    useActionDispatchOnMount(true, makeRegistrationStoreSettingsFetchAction);

    return (
        <ErrorBoundary>
            <form className={classes.containerForm} onSubmit={handleSubmit}>
                <div className={classes.container}>
                    <div className={classes.content}>
                        <Text className={classes.text} typography={TextTypography.TITLE_CHANGE}>
                            {t('onboarding:settings.settingDelivery')}
                        </Text>
                        <div className={classes.line}>
                            <HorizontalDivider />
                        </div>
                        {/*{deliveryInfo && pickUpInfo && (*/}
                        {/* <>
                            <Box mt={2}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <Field
                                            name="enabledSpecificDelivery"
                                            component={SwitchControlField}
                                            label={
                                                <Text typography={TextTypography.PARA_ACCENT} className={classes.label}>
                                                    {t('onboarding:settings.deliveryCard.specificDelivery')}
                                                </Text>
                                            }
                                        />
                                    </Grid>
                                </Grid>
                                <div className={classes.infoContainer}>
                                    <Grid item xs={12} className={classes.subTitle}>
                                        <Text typography={TextTypography.PARA_CHANGE} className={classes.subTitle}>
                                            {t('onboarding:settings.deliveryCard.specificDeliveryDetails')}
                                        </Text>
                                    </Grid>
                                    {deliverySwitch && (
                                        <div>
                                            <Text typography={TextTypography.BODY} className={classes.subTitle}>
                                                {t('onboarding:settings.deliveryCard.specificDeliveryDetailsSubtitle')}
                                            </Text>
                                        </div>
                                    )}
                                </div>
                            </Box>
                            <div className={classes.line}>
                                <HorizontalDivider />
                            </div>
                        </> */}
                        {/*)}*/}
                        <Grid
                            container
                            direction={'row'}
                            justify={'flex-start'}
                            spacing={3}
                            className={classes.deliveryContent}
                        >
                            <Grid xs={12} sm={6} className={classes.settingsItem}>
                                <div className={classes.link} onClick={props.onPickupClick}>
                                    <InfoButton
                                        icon={PickUpIcon}
                                        info={pickUpInfo}
                                        color="rgba(255, 114, 0, 0.1)"
                                        title={t('onboarding:settings.pickup')}
                                    />
                                </div>
                            </Grid>
                            <Grid xs={12} sm={6} className={classes.settingsItem}>
                                <div className={classes.link} onClick={props.onDeliveryClick}>
                                    <InfoButton
                                        icon={DeliveryIcon}
                                        info={deliveryInfo}
                                        color="rgba(117, 195, 69, 0.1)"
                                        title={t('onboarding:settings.delivery')}
                                    />
                                </div>
                            </Grid>
                        </Grid>
                        <div className={classes.line}>
                            <HorizontalDivider />
                        </div>

                        <Box pb={2}>
                            <Text typography={TextTypography.PARA_CHANGE}>
                                {t('onboarding:settings.deliveryCommentTitle')}
                            </Text>
                        </Box>
                        <div className={classes.wysiwygField}>
                            <Field
                                name="deliveryComment"
                                component={RichTextControlField}
                                label={t('onboarding:settings.deliveryComment')}
                            />
                        </div>
                    </div>
                    <div className={classes.content}>
                        <Text className={classes.text} typography={TextTypography.TITLE_CHANGE}>
                            {t('onboarding:settings.settingPayment')}
                        </Text>

                        <Grid container direction={'row'} justify={'flex-start'} spacing={3}>
                            <Grid xs={12} sm={6} md={4} className={classes.settingsItem}>
                                <div className={classes.link} onClick={props.onCashClick}>
                                    <InfoButton
                                        icon={CashIcon}
                                        info={cashInfo}
                                        color="rgba(255, 163, 16, 0.15)"
                                        title={t('onboarding:settings.cash')}
                                    />
                                </div>
                            </Grid>
                            <Grid xs={12} sm={6} md={4} className={classes.settingsItem}>
                                <div className={classes.link} onClick={props.onCardClick}>
                                    <InfoButton
                                        icon={CardsIcon}
                                        info={cardInfo}
                                        color="rgba(166, 73, 253, 0.1)"
                                        title={t('onboarding:settings.card')}
                                    />
                                </div>
                            </Grid>
                            <Grid xs={12} sm={6} md={4} className={classes.settingsItem}>
                                <div className={classes.link} onClick={props.onOnlineClick}>
                                    <InfoButton
                                        icon={OnlineIcon}
                                        info={yandexCheckoutFormValues.isEnabled ? 'ЮKassa' : null}
                                        color="rgba(38, 151, 255, 0.1)"
                                        title={t('onboarding:settings.online')}
                                    />
                                </div>
                            </Grid>
                            {errorMessage && (
                                <Grid item xs={12}>
                                    <FormErrorMessage error={errorMessage} />
                                </Grid>
                            )}
                        </Grid>
                        <Box pb={2} pt={3}>
                            <Text typography={TextTypography.PARA_CHANGE}>
                                {t('onboarding:settings.paymentCommentTitle')}
                            </Text>
                        </Box>
                        <div className={classes.wysiwygField}>
                            <Field
                                name="paymentComment"
                                component={RichTextControlField}
                                label={t('onboarding:settings.paymentComment')}
                            />
                        </div>
                    </div>
                </div>
                <div className={classes.footerInfo}>
                    <div className={classes.editSubmit}>
                        <div style={{ marginRight: 8, width: '100%' }}>
                            <LargeButton
                                disabled={!isUIEnabled}
                                color={LargeButtonColor.LIGHT}
                                onClick={goToVendorDashboard}
                                type="submit"
                            >
                                {t('onboarding:footer.close')}
                            </LargeButton>
                        </div>
                        <div style={{ marginLeft: 8, width: '100%' }}>
                            <LargeButton color={LargeButtonColor.SUCCESS} disabled={!isUIEnabled} type="submit">
                                {t('common:addressDialog.save')}
                            </LargeButton>
                        </div>
                    </div>
                </div>
            </form>
        </ErrorBoundary>
    );
};

export const StoreVendorSettingsEditForm = connect(mapStateToEditFormProps)(
    reduxForm({
        form: Form.STORE_SETTING_EDIT_FORM,
        initialValues: {},
    })(DefaultStoreSettingsForm),
);
